// The ONE central query-key factory (docs/09-frontend-surface.md §6). No inline
// query keys anywhere: stable, structured keys are what make SSE-driven (§5)
// and mutation-driven cache invalidation reliable. Keys are noun-correct and
// mirror the `/v1` resources; filters (e.g. `agent_id`) are carried in the key
// so a targeted invalidation hits exactly the affected lists.

type Filters = Readonly<Record<string, unknown>>;

function resource(name: string) {
  return {
    all: [name] as const,
    list: (filters: Filters = {}) => [name, "list", filters] as const,
    detail: (id: string) => [name, "detail", id] as const,
  };
}

export const queryKeys = {
  agents: resource("agents"),
  agentVersions: resource("agent-versions"),
  batches: {
    ...resource("batches"),
    preview: (fingerprint: string) =>
      ["batches", "preview", fingerprint] as const,
  },
  connections: resource("connections"),
  dashboards: resource("dashboards"),
  transcripts: {
    ...resource("transcripts"),
    // Immutable precomputed waveform peaks subresource read by GET
    // `{id}/audio/peaks`; keyed by transcript id.
    audioPeaks: (id: string) => ["transcripts", "audio-peaks", id] as const,
    // Immutable frozen variables subresource read by GET `{id}/variables`.
    variables: (id: string) => ["transcripts", "variables", id] as const,
  },
  traces: resource("traces"),
  markers: {
    ...resource("markers"),
    reviewSummary: (filters: Filters) =>
      ["markers", "review-summary", filters] as const,
    allForAgent: (agentId: string) =>
      ["markers", "all-for-agent", agentId] as const,
  },
  markerRevisions: resource("marker-revisions"),
  personas: {
    ...resource("personas"),
    allForAgent: (agentId: string) =>
      ["personas", "all-for-agent", agentId] as const,
  },
  // Read-only `/v1/phone-numbers` projection of the org's synced telephony
  // inventory; the persona editor's pinned-number picker reads it.
  phoneNumbers: resource("phone-numbers"),
  scenarios: {
    ...resource("scenarios"),
    allForAgent: (agentId: string) =>
      ["scenarios", "all-for-agent", agentId] as const,
    // Transient authoring preview (`POST /v1/scenarios/{id}:preview`). The
    // fingerprint carries the debounced request body so an edited draft,
    // persona, or row is a new key rather than a silently stale cache hit.
    preview: (scenarioId: string, fingerprint: string) =>
      ["scenarios", "preview", scenarioId, fingerprint] as const,
  },
  scenarioRows: resource("scenario-rows"),
  scenarioSets: resource("scenario-sets"),
  templates: resource("templates"),
  // Effective agent-level firing configuration read by GET
  // `:marker-configuration` and echoed by POST `:configure-markers`; keyed by
  // agent id via `.detail(agentId)`.
  markerConfiguration: resource("marker-configuration"),
  answers: resource("answers"),
  alignment: resource("alignment"),
  notes: resource("notes"),
  variables: {
    ...resource("variables"),
    allForAgent: (agentId: string) =>
      ["variables", "all-for-agent", agentId] as const,
    references: (id: string) => ["variables", "references", id] as const,
    revisions: (id: string) => ["variables", "revisions", id] as const,
  },
  apiKeys: {
    ...resource("api-keys"),
    scopeCatalog: (target: { agent_id?: string; team_id?: string }) =>
      ["api-keys", "scope-catalog", target] as const,
  },
  invitations: resource("invitations"),
  integrationCredentials: resource("integration-credentials"),
  memberships: resource("memberships"),
  organizations: resource("organizations"),
  // Read-only `/v1/organizations/{id}/limits` subresource; keyed by org id.
  organizationLimits: resource("organization-limits"),
  users: resource("users"),
  teams: resource("teams"),
  retentionPolicies: resource("retention-policies"),
  auditLogs: resource("audit-logs"),
  metrics: resource("metrics"),
  gateRuns: resource("gate-runs"),
  alertChannels: resource("alert-channels"),
  alertRules: resource("alert-rules"),
  alertEvents: resource("alert-events"),
  slack: {
    all: ["slack"] as const,
    installation: ["slack", "installation"] as const,
    channels: (query: string) => ["slack", "channels", { query }] as const,
  },
  me: ["me"] as const,
  // Read-only `/v1/billing/entitlement` projection (hosted installs only);
  // the checkout/return screens poll it, so it stays a single stable key.
  billingEntitlement: ["billing", "entitlement"] as const,
  // Read-only `/v1/system/license` status (docs/04 §5a); a single stable key
  // shared by the settings page and the app-wide banner.
  license: ["system", "license"] as const,
  queueHealth: ["system", "queue-health"] as const,
} as const;
